import React, { useRef, useLayoutEffect, useState, useEffect } from 'react';
import { RotatingLines } from 'react-loader-spinner';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { getToolCallMeta } from '../../utils/toolCallMetadata';

/**
 * Inline tool call indicator - matches mobile ToolCallIndicator design.
 * Compact pill with connector icon, status text, mask-based shimmer when in progress (like transcribing in ChatInput).
 * Uses measured text width so proportional fonts size correctly (e.g. "Working with GitHub" is wider than "Listing repositories" despite similar char count).
 */
const InlineToolCallIndicator = ({
  isDark = false,
  status,
  isComplete = false,
  success = true,
  functionName,
  recordCount,
  isFirst = false,
  codePreview,
}) => {
  const borderColor = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)';
  const backgroundColor = isDark ? '#121212' : '#f4f4f5';
  const iconColor = isDark ? '#ffffff' : '#111111';
  const spinnerColor = isDark ? '#ffffff' : '#111111';
  const successColor = isDark ? '#ffffff' : '#111111';
  const errorColor = isDark ? '#71717a' : '#9ca3af';

  const meta = getToolCallMeta(functionName, { success, recordCount });
  const Icon = meta.Icon;
  const displayStatus =
    status && status.trim()
      ? status
      : isComplete
        ? meta.getCompleteStatus(success, recordCount)
        : meta.getInProgressStatus();

  const showShimmer = !isComplete;
  const isError = !success || (status && status.toLowerCase().match(/error|failed/));

  const measureRef = useRef(null);
  const [textWidth, setTextWidth] = useState(0);

  useLayoutEffect(() => {
    if (measureRef.current) {
      const w = measureRef.current.getBoundingClientRect().width;
      setTextWidth(Math.ceil(w) + 2);
    }
  }, [displayStatus]);

  // Fallback when measurement not yet available (avoids truncation like "Building artifa")
  const MIN_TEXT_WIDTH = 140;
  const effectiveTextWidth = textWidth > 0 ? textWidth : MIN_TEXT_WIDTH;

  return (
    <>
    <div
      className={`relative inline-flex items-center gap-2 py-1.5 pl-2.5 pr-2.5 rounded-full border mb-2 leading-none ${isFirst ? 'mt-0.5' : 'mt-2'}`}
      style={{
        borderColor,
        backgroundColor,
      }}
    >
      <span className="flex items-center justify-center w-[16px] h-[16px] rounded-[4px] flex-shrink-0" style={{ color: meta.connectorId === 'hubspot' ? '#FF7A59' : iconColor }}>
        <Icon size={14} strokeWidth={meta.connectorId === 'unknown' ? 2 : undefined} />
      </span>
      <span className="relative inline-block text-[13px] font-medium h-4 flex items-center flex-shrink-0" style={{ fontFamily: 'system-ui, -apple-system, sans-serif', minWidth: effectiveTextWidth }}>
        <span
          ref={measureRef}
          aria-hidden
          className="invisible absolute left-0 top-0 whitespace-nowrap"
          style={{ fontFamily: 'inherit' }}
        >
          {displayStatus}
        </span>
        {showShimmer ? (
          <span className="block whitespace-nowrap" style={{ width: 'fit-content', minWidth: 0 }}>
            <SkeletonTheme
              baseColor={isDark ? '#404040' : '#6b7280'}
              highlightColor={isDark ? '#ffffff' : '#000000'}
            >
              <div
                style={{
                  width: `${effectiveTextWidth}px`,
                  height: '16px',
                  display: 'inline-block',
                  overflow: 'hidden',
                  verticalAlign: 'middle',
                }}
              >
                <div
                  style={{
                    width: effectiveTextWidth,
                    height: '16px',
                    overflow: 'hidden',
                    maskImage: `url("data:image/svg+xml,${encodeURIComponent(
                      `<svg width="${effectiveTextWidth}" height="16" viewBox="0 0 ${effectiveTextWidth} 16" xmlns="http://www.w3.org/2000/svg">
                        <text x="0" y="12" font-family="system-ui, -apple-system, sans-serif" font-size="13" font-weight="500" fill="black">${displayStatus.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')}</text>
                      </svg>`
                    )}")`,
                    WebkitMaskImage: `url("data:image/svg+xml,${encodeURIComponent(
                      `<svg width="${effectiveTextWidth}" height="16" viewBox="0 0 ${effectiveTextWidth} 16" xmlns="http://www.w3.org/2000/svg">
                        <text x="0" y="12" font-family="system-ui, -apple-system, sans-serif" font-size="13" font-weight="500" fill="black">${displayStatus.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')}</text>
                      </svg>`
                    )}")`,
                    maskSize: 'contain',
                    WebkitMaskSize: 'contain',
                    maskRepeat: 'no-repeat',
                    WebkitMaskRepeat: 'no-repeat',
                    maskPosition: 'left center',
                    WebkitMaskPosition: 'left center',
                  }}
                >
                  <Skeleton
                    width={effectiveTextWidth}
                    height={16}
                    style={{ borderRadius: 0, display: 'block', margin: 0, padding: 0, lineHeight: 1, minHeight: 16 }}
                  />
                </div>
              </div>
            </SkeletonTheme>
          </span>
        ) : (
          <span
            className="whitespace-nowrap"
            style={{
              color: isError ? errorColor : successColor,
            }}
          >
            {displayStatus}
          </span>
        )}
      </span>
      {showShimmer && (
        <span className="flex-shrink-0 flex items-center justify-center w-5 h-5">
          <RotatingLines
            visible={true}
            width="18"
            strokeWidth="3"
            strokeColor={spinnerColor}
            animationDuration="0.75"
            ariaLabel="tool-loading"
          />
        </span>
      )}
    </div>
    {showShimmer && (codePreview ? <CodePreviewBlock code={codePreview} isDark={isDark} /> : <CodePreviewPlaceholder isDark={isDark} />)}
    </>
  );
};

const CodePreviewPlaceholder = ({ isDark }) => (
  <div
    style={{
      padding: '8px 10px',
      marginBottom: 8,
      fontSize: 11,
      fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, monospace',
      color: isDark ? '#6b7280' : '#9ca3af',
      backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)',
      border: `1px dashed ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
      borderRadius: 8,
    }}
  >
    Streaming code…
  </div>
);

/**
 * Extract displayable code/html from raw streaming JSON (e.g. {"html":"<!DOCTYPE... or {"code":"let slide = ...)
 */
function extractCodeFromStreamingJson(raw) {
  if (!raw || typeof raw !== 'string') return raw;
  // Try full parse first (when complete)
  try {
    const parsed = JSON.parse(raw);
    return parsed.html || parsed.code || parsed.chartConfig || raw;
  } catch {
    // Streaming - take everything after the value start (simple prefix strip)
    const htmlPrefix = '"html":"';
    const codePrefix = '"code":"';
    const idx = raw.indexOf(htmlPrefix);
    if (idx >= 0) return raw.slice(idx + htmlPrefix.length).replace(/\\n/g, '\n').replace(/\\"/g, '"');
    const codeIdx = raw.indexOf(codePrefix);
    if (codeIdx >= 0) return raw.slice(codeIdx + codePrefix.length).replace(/\\n/g, '\n').replace(/\\"/g, '"');
  }
  return raw;
}

const CodePreviewBlock = ({ code, isDark }) => {
  const scrollRef = useRef(null);
  const displayCode = extractCodeFromStreamingJson(code);
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [displayCode]);

  // Show tail of the code so user sees latest output
  const tail = displayCode.length > 800 ? displayCode.slice(-800) : displayCode;

  return (
    <div
      ref={scrollRef}
      style={{
        maxHeight: 120,
        overflow: 'hidden',
        borderRadius: 8,
        padding: '8px 10px',
        marginBottom: 8,
        fontSize: 11,
        lineHeight: 1.45,
        fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, monospace',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-all',
        color: isDark ? '#9ca3af' : '#6b7280',
        backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)',
        border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
        maskImage: 'linear-gradient(to bottom, transparent 0%, black 20%, black 100%)',
        WebkitMaskImage: 'linear-gradient(to bottom, transparent 0%, black 20%, black 100%)',
      }}
    >
      {tail}
    </div>
  );
};

export default InlineToolCallIndicator;
